/**
 * The clock lever.
 *
 * A real <input type="range"> does the work - visually hidden but focusable,
 * labelled, and draggable. The painted cap and slot are drawn on top of it.
 * Arrow keys step by 5, Page Up/Down by 25, Home/End jump to the stops.
 */

import { CLOCK_MIN, LEVER_UNLOCK_COUNT, WARRANTY_CLOCK } from '../../data/balance.ts';
import { redlineClock, type Modifiers } from '../../sim/formulas.ts';
import type { GameState } from '../../sim/state.ts';
import { byId, setText } from '../dom.ts';

export interface ClockPanel {
  update(state: GameState, mods: Modifiers): void;
}

const ARROW_STEP = 5;
const PAGE_STEP = 25;

export function createClockPanel(onChange: (value: number) => void): ClockPanel {
  const lever = byId('lever');
  const input = byId<HTMLInputElement>('clock-input');
  const cap = byId('lever-cap');
  const fill = byId('lever-fill');
  const ticks = byId('lever-ticks');
  const value = byId('clock-value');
  const note = byId('clock-note');
  const warranty = byId('warranty');
  const specLimit = byId('spec-limit');
  const scaleMax = byId('scale-max');
  const scaleMin = byId('scale-min');

  setText(scaleMin, String(CLOCK_MIN));

  input.addEventListener('input', () => onChange(Number(input.value)));

  input.addEventListener('keydown', (event) => {
    const current = Number(input.value);
    let next: number | null = null;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        next = current - ARROW_STEP;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        next = current + ARROW_STEP;
        break;
      case 'PageDown':
        next = current - PAGE_STEP;
        break;
      case 'PageUp':
        next = current + PAGE_STEP;
        break;
      case 'Home':
        next = Number(input.min);
        break;
      case 'End':
        next = Number(input.max);
        break;
      default:
        return;
    }
    event.preventDefault();
    onChange(next);
  });

  let tickCount = -1;

  function update(state: GameState, mods: Modifiers): void {
    const unlocked = state.unlocks.lever === true;
    const max = mods.clockCap;

    if (input.disabled === unlocked) input.disabled = !unlocked;
    lever.classList.toggle('is-locked', !unlocked);

    if (Number(input.max) !== max) {
      input.max = String(max);
      setText(scaleMax, String(max));
    }
    if (Number(input.value) !== state.clock) input.value = String(state.clock);

    // Tick marks every 25%, rebuilt only when the ceiling actually moves.
    const wanted = Math.floor((max - CLOCK_MIN) / 25) + 1;
    if (wanted !== tickCount) {
      tickCount = wanted;
      ticks.replaceChildren();
      for (let i = 0; i < wanted; i++) {
        const t = document.createElement('span');
        t.className = 'lever__tick';
        ticks.append(t);
      }
    }

    const fraction = (state.clock - CLOCK_MIN) / (max - CLOCK_MIN);
    const percent = `${(fraction * 100).toFixed(2)}%`;
    if (cap.style.left !== percent) cap.style.left = percent;
    if (fill.style.width !== percent) fill.style.width = percent;

    const redline = redlineClock(mods);
    fill.classList.toggle('is-warn', state.clock > 100 && state.clock < redline);
    fill.classList.toggle('is-redline', state.clock >= redline);

    setText(value, String(state.clock));

    if (!unlocked) {
      const remaining = LEVER_UNLOCK_COUNT - (state.generators[0] ?? 0);
      setText(note, 'Spec limit');
      setText(
        specLimit,
        `Locked at 100%. Install ${remaining > 0 ? remaining : 0} more Neurons to release the stop.`,
      );
      if (specLimit.hidden) specLimit.hidden = false;
    } else if (specLimit.hidden === false && state.stats.meltdowns > 0) {
      specLimit.hidden = true;
    } else {
      setText(note, state.clock > 100 ? 'Over spec' : state.clock < 100 ? 'Under spec' : 'At spec');
      setText(specLimit, 'Manufacturer spec is 100%. The rig will go further.');
    }

    const voids = state.clock > WARRANTY_CLOCK;
    if (warranty.hidden === voids) warranty.hidden = !voids;
  }

  return { update };
}
